import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Button } from "@/lib/components/Button/Button";
import { IconLoader2, IconPlus, IconSend } from "@tabler/icons-react";
import {
  useActiveScriptChatQuery,
  useCreateScriptChatMutation,
  sendScriptChatMessage,
  voiceOverQueryKeys,
} from "@/lib/queries/voice-over-queries";
import { cn } from "@/lib/utils";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/lib/components/Dialog";
import { useQueryClient } from "@tanstack/react-query";
import { NavigationButton } from "../NavigationButton";
import { useUser } from "@clerk/react";

type ChatMessage = {
  externalId: string;
  createdAt: string;
  role: "user" | "model";
  content: string;
  isStreaming?: boolean;
  shouldAnimate?: boolean;
};

type ChatCacheData = {
  chat: {
    externalId: string;
    messages: {
      externalId: string;
      createdAt: string;
      role: "user" | "model";
      content: string;
    }[];
  };
};

function typeWords(
  text: string,
  onWord: (word: string) => void,
  delay: number = 50,
): Promise<void> {
  return new Promise((resolve) => {
    const words = text.match(/\S+\s*/g) || [];
    let index = 0;

    if (words.length === 0) {
      resolve();
      return;
    }

    const interval = setInterval(() => {
      if (index < words.length) {
        onWord(words[index]!);
        index++;
      } else {
        clearInterval(interval);
        resolve();
      }
    }, delay);
  });
}

function MessageContent({
  content,
  shouldAnimate,
}: {
  content: string;
  shouldAnimate?: boolean;
}) {
  const words = useMemo(() => content.match(/\S+\s*/g) || [], [content]);

  if (!shouldAnimate) {
    return <span className="whitespace-pre-wrap">{content}</span>;
  }

  return (
    <span className="whitespace-pre-wrap">
      {words.map((word, index) => (
        <span
          key={index}
          className="inline animate-fade-in"
          style={{
            animationDelay: `${index * 20}ms`,
            animationFillMode: "backwards",
          }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

const QUICK_ACTIONS = [
  {
    buttonText: "Shorter",
    messageText: "Make it 25% shorter",
  },
  {
    buttonText: "Longer",
    messageText: "Make it 25% longer",
  },
  {
    buttonText: "More Formal",
    messageText: "Make it more formal",
  },
  {
    buttonText: "More Casual",
    messageText: "Make it more casual",
  },
];

function ScriptWriter() {
  const teamExternalId = useEditorStore((state) => state.teamExternalId);
  const videoExternalId = useEditorStore((state) => state.videoExternalId);
  const updateVoiceoverTabState = useEditorStore(
    (state) => state.service.updateVoiceoverTabState,
  );
  const updateVoiceOverForm = useEditorStore(
    (state) => state.service.updateVoiceOverForm,
  );
  const inputValue = useEditorStore(
    (state) => state.voiceoverTabState.scriptWriterInput,
  );

  const setInputValue = useCallback(
    (value: string) => {
      updateVoiceoverTabState((state) => {
        state.scriptWriterInput = value;
      });
    },
    [updateVoiceoverTabState],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatExternalId, setChatExternalId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isNewChatDialogOpen, setIsNewChatDialogOpen] = useState(false);
  const [isCreatingNewChat, setIsCreatingNewChat] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasInitializedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const chatQuery = useActiveScriptChatQuery(
    teamExternalId,
    videoExternalId,
    !!teamExternalId && !!videoExternalId && !hasInitializedRef.current,
  );

  const hasUserMessage = useMemo(
    () => messages.some((msg) => msg.role === "user"),
    [messages],
  );

  useEffect(() => {
    if (!isInitializing) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    }
  }, [messages, isInitializing]);

  const streamAssistantMessage = useCallback(
    async (
      messageId: string,
      createdAt: string,
      content: string,
      onComplete?: () => void,
    ) => {
      setMessages((prev) => [
        ...prev,
        {
          externalId: messageId,
          createdAt,
          role: "model",
          content: "",
          isStreaming: true,
          shouldAnimate: true,
        },
      ]);

      let accumulated = "";
      await typeWords(content, (word) => {
        accumulated += word;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.externalId === messageId
              ? { ...msg, content: accumulated }
              : msg,
          ),
        );
      });

      setMessages((prev) =>
        prev.map((msg) =>
          msg.externalId === messageId ? { ...msg, isStreaming: false } : msg,
        ),
      );

      onComplete?.();
    },
    [],
  );

  const queryClient = useQueryClient();
  const { user } = useUser();

  const { mutate: createScriptChat } = useCreateScriptChatMutation({
    teamExternalId,
    videoExternalId,
    onMutate: () => {
      queryClient.removeQueries({
        queryKey: voiceOverQueryKeys.getActiveScriptChat(
          user?.id,
          teamExternalId,
          videoExternalId,
        ),
      });
    },
    onSuccess: async (data) => {
      const { chat } = data;
      setChatExternalId(chat.externalId);
      hasInitializedRef.current = true;

      queryClient.setQueryData(
        voiceOverQueryKeys.getActiveScriptChat(
          user?.id,
          teamExternalId,
          videoExternalId,
        ),
        { chat },
      );

      setIsInitializing(false);
      setIsCreatingNewChat(false);

      if (chat.messages.length > 0) {
        const firstMessage = chat.messages[0]!;
        setMessages([]);
        await streamAssistantMessage(
          firstMessage.externalId,
          firstMessage.createdAt,
          firstMessage.content,
        );
      }
    },
    onError: (error) => {
      setIsInitializing(false);
      setIsCreatingNewChat(false);
      console.error("Failed to create chat:", error);
    },
  });

  const handleCreateNewChat = useCallback(() => {
    abortControllerRef.current?.abort();

    setMessages([]);
    setChatExternalId(null);
    setInputValue("");
    setIsSending(false);
    setIsCreatingNewChat(true);
    setIsNewChatDialogOpen(false);

    createScriptChat();
  }, [createScriptChat, setInputValue]);

  useEffect(() => {
    if (!teamExternalId || !videoExternalId || hasInitializedRef.current)
      return;

    if (!chatQuery.isFetched) return;

    if (chatQuery.data) {
      const { chat } = chatQuery.data;
      setChatExternalId(chat.externalId);

      const visibleMessages = chat.messages.map((msg) => ({
        externalId: msg.externalId,
        createdAt: msg.createdAt,
        role: msg.role,
        content: msg.content,
        isStreaming: false,
        shouldAnimate: false,
      }));

      setMessages(visibleMessages);
      hasInitializedRef.current = true;
      setIsInitializing(false);
      return;
    }

    hasInitializedRef.current = true;
    createScriptChat();
  }, [
    chatQuery.data,
    chatQuery.error,
    chatQuery.isFetched,
    teamExternalId,
    videoExternalId,
    createScriptChat,
  ]);

  const chatQueryKey = voiceOverQueryKeys.getActiveScriptChat(
    user?.id,
    teamExternalId,
    videoExternalId,
  );

  const addMessageToCache = useCallback(
    (message: {
      externalId: string;
      createdAt: string;
      role: "user" | "model";
      content: string;
    }) => {
      queryClient.setQueryData<ChatCacheData>(chatQueryKey, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          chat: {
            ...oldData.chat,
            messages: [...oldData.chat.messages, message],
          },
        };
      });
    },
    [queryClient, chatQueryKey],
  );

  const handleSend = useCallback(
    async (quickAction?: string) => {
      const message = quickAction ?? inputValue.trim();
      if (!message || isSending || !chatExternalId) return;

      setInputValue("");
      setIsSending(true);

      abortControllerRef.current = new AbortController();

      const tempUserMessageId = `temp-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          externalId: tempUserMessageId,
          createdAt: new Date().toISOString(),
          role: "user",
          content: message,
          isStreaming: false,
        },
      ]);

      try {
        await sendScriptChatMessage(
          teamExternalId,
          chatExternalId,
          message,
          {
            onUserMessage: (message) => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.externalId === tempUserMessageId
                    ? {
                        externalId: message.externalId,
                        createdAt: message.createdAt,
                        role: "user",
                        content: message.content,
                        isStreaming: false,
                      }
                    : msg,
                ),
              );
              addMessageToCache(message);
            },
            onChunk: () => {},
            onDone: async (message) => {
              addMessageToCache(message);
              await streamAssistantMessage(
                message.externalId,
                message.createdAt,
                message.content,
              );
              setIsSending(false);
            },
            onError: (error) => {
              console.error("Stream error:", error);

              setMessages((prev) =>
                prev.filter((msg) => msg.externalId !== tempUserMessageId),
              );
              setIsSending(false);
            },
          },
          abortControllerRef.current.signal,
        );
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          console.error("Failed to send message:", error);

          setMessages((prev) =>
            prev.filter((msg) => msg.externalId !== tempUserMessageId),
          );
        }
        setIsSending(false);
      } finally {
        abortControllerRef.current = null;
      }
    },
    [
      inputValue,
      isSending,
      chatExternalId,
      teamExternalId,
      streamAssistantMessage,
      addMessageToCache,
      setInputValue,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleInsert = useCallback(
    (content: string) => {
      const htmlContent = content
        .split("\n")
        .map((line) => `<p>${line || "<br>"}</p>`)
        .join("");

      updateVoiceOverForm((form) => {
        form.script = htmlContent;
      });
    },
    [updateVoiceOverForm],
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [inputValue]);

  return (
    <div className="absolute top-0 left-full z-50 ml-3 flex h-full w-[calc(100vw-100%-2.25rem)] animate-fade-in-fast">
      <div className="flex h-full w-full max-w-[400px] flex-col rounded-2xl bg-white shadow-card">
        <NavigationButton
          className="py-4"
          text="Scriptwriter"
          onClose={() => {
            abortControllerRef.current?.abort();
            updateVoiceoverTabState((state) => {
              state.view = "CREATE_VOICEOVER";
            });
          }}
          titleAction={
            <Dialog
              open={isNewChatDialogOpen}
              onOpenChange={setIsNewChatDialogOpen}
            >
              <DialogTrigger asChild>
                <Button
                  size="iconSm"
                  variant="ghost"
                  disabled={isInitializing || isSending || isCreatingNewChat}
                  className="[&_svg]:stroke-[url(#icon-gradient)]"
                >
                  <IconPlus stroke={2.5} />
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader className="w-full">
                  <DialogTitle>Start New Conversation</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to start a new conversation? Your
                    current conversation will be replaced.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="mt-0">
                  <Button
                    onClick={handleCreateNewChat}
                    disabled={isCreatingNewChat}
                  >
                    {isCreatingNewChat ? "Creating..." : "Start New"}
                  </Button>
                  <DialogClose asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isCreatingNewChat}
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 pt-6 pb-4">
            {isInitializing ? (
              <div className="flex h-full items-center justify-center">
                <LoadingSpinner className="h-8 w-8" />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {messages.map((message, index) => (
                  <div
                    key={message.externalId}
                    className={cn(
                      "flex flex-col",
                      message.role === "user" ? "items-end" : "items-start",
                    )}
                  >
                    <div
                      className={cn(
                        "mb-3 max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed wrap-break-word text-text-primary",
                        message.role === "model" &&
                          "border border-primary-400/5 bg-primary-100",
                        message.role === "user" &&
                          "border border-tertiary-400/5 bg-tertiary-100",
                      )}
                    >
                      {message.role === "model" ? (
                        <MessageContent
                          content={message.content}
                          shouldAnimate={message.shouldAnimate}
                        />
                      ) : (
                        <div className="whitespace-pre-wrap">
                          {message.content}
                        </div>
                      )}
                    </div>
                    {message.role === "model" &&
                      !message.isStreaming &&
                      message.content &&
                      index > 0 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              onClick={() => handleInsert(message.content)}
                              size="xs"
                              variant="gradientBorder"
                            >
                              Use
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Use script for the voiceover
                          </TooltipContent>
                        </Tooltip>
                      )}
                  </div>
                ))}
                {isSending &&
                  messages.length > 1 &&
                  messages[messages.length - 1]?.role === "user" && (
                    <div className="flex flex-col items-start">
                      <LoadingSpinner className="h-6 w-6" />
                    </div>
                  )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
          <div className="border-t border-gray-200 px-4 py-3 pt-4">
            {hasUserMessage && (
              <div className="flex flex-wrap gap-1.5 pb-2.5">
                {QUICK_ACTIONS.map((action) => (
                  <Button
                    key={action.buttonText}
                    size="pill"
                    variant="tertiarySecondary"
                    onClick={() => handleSend(action.messageText)}
                    disabled={isSending || isInitializing}
                  >
                    {action.buttonText}
                  </Button>
                ))}
              </div>
            )}
            <div className="flex items-start gap-2">
              <div className="flex flex-1 flex-col rounded-md border border-gray-200 bg-gray-50 px-3 pt-2 pb-2">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  rows={1}
                  disabled={isSending || isInitializing}
                  className="max-h-[120px] min-h-16 w-full resize-none bg-transparent text-sm text-text-primary placeholder:text-muted-foreground focus:outline-none disabled:opacity-50"
                />
              </div>
              <Button
                size="iconSm"
                variant="default"
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isSending || isInitializing}
                className="h-9 w-9 shrink-0"
              >
                {isSending ? (
                  <IconLoader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <IconSend stroke={2} className="mt-0.25 h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScriptWriter;
